import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { jobService } from '@/lib/api/services/job'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Job,
  JobStats,
  CreateJob,
  UpdateJob,
  UpdateJobStatus,
  JobFilterParams
} from '@/lib/api/types'

// Query keys for caching
export const jobKeys = {
  all: ['jobs'] as const,
  lists: () => [...jobKeys.all, 'list'] as const,
  list: (filters?: JobFilterParams) => [...jobKeys.lists(), filters] as const,
  details: () => [...jobKeys.all, 'detail'] as const,
  detail: (id: string) => [...jobKeys.details(), id] as const,
  filtered: (params: PaginationParams, filters: JobFilterParams) =>
    [...jobKeys.all, 'filtered', params, filters] as const,
  search: (query: string) => [...jobKeys.all, 'search', query] as const,
  stats: () => [...jobKeys.all, 'stats'] as const
}

// Hook to filter jobs with pagination
export function useFilterJobs(
  params: PaginationParams = { page: 1, size: 10 },
  filters: JobFilterParams = {}
) {
  return useQuery<ApiResponse<PaginatedResponse<Job>>, Error>({
    queryKey: jobKeys.filtered(params, filters),
    queryFn: () => jobService.filterJobs(params, filters)
  })
}

// Hook to fetch a single job
export function useJob(jobId?: string) {
  return useQuery<ApiResponse<Job>, Error>({
    queryKey: jobKeys.detail(jobId!),
    queryFn: () => jobService.getJobById(jobId!),
    enabled: !!jobId
  })
}

// Hook to create a job
export function useCreateJob() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<Job>, Error, CreateJob>({
    mutationFn: (data) => jobService.createJob(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() })
      queryClient.invalidateQueries({ queryKey: jobKeys.all })
      queryClient.invalidateQueries({ queryKey: jobKeys.stats() })
    }
  })
}

// Hook to update a job
export function useUpdateJob() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<Job>,
    Error,
    { jobId: string; data: UpdateJob }
  >({
    mutationFn: ({ jobId, data }) =>
      jobService.updateJob(jobId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() })
      queryClient.invalidateQueries({ queryKey: jobKeys.all })
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(variables.jobId) })
      queryClient.invalidateQueries({ queryKey: jobKeys.stats() })
    }
  })
}

// Hook to update job status
export function useUpdateJobStatus() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<null>,
    Error,
    { jobId: string; data: UpdateJobStatus }
  >({
    mutationFn: ({ jobId, data }) =>
      jobService.updateJobStatus(jobId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() })
      queryClient.invalidateQueries({ queryKey: jobKeys.all })
      queryClient.invalidateQueries({ queryKey: jobKeys.stats() })
    }
  })
}

// Hook to delete a job
export function useDeleteJob() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<null>,
    Error,
    string
  >({
    mutationFn: (jobId) =>
      jobService.deleteJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() })
      queryClient.invalidateQueries({ queryKey: jobKeys.all })
      queryClient.invalidateQueries({ queryKey: jobKeys.stats() })
    }
  })
}

// Hook to fetch published jobs count
export function usePublishedJobsCount() {
  return useQuery<ApiResponse<number>, Error>({
    queryKey: [...jobKeys.stats(), 'published'],
    queryFn: () => jobService.getPublishedJobsCount()
  })
}

// Hook to fetch draft jobs count
export function useDraftJobsCount() {
  return useQuery<ApiResponse<number>, Error>({
    queryKey: [...jobKeys.stats(), 'draft'],
    queryFn: () => jobService.getDraftJobsCount()
  })
}

// Hook to search jobs
export function useSearchJobs(
  searchBy?: string,
  enabled: boolean = true
) {
  return useQuery<ApiResponse<Job[]>, Error>({
    queryKey: jobKeys.search(searchBy || ''),
    queryFn: () => jobService.searchJobs(searchBy),
    enabled: enabled && !!searchBy && searchBy.length > 0,
    staleTime: 2 * 60 * 1000 // 2 minutes
  })
}

// Hook to fetch job stats
export function useJobStats() {
  return useQuery<JobStats, Error>({
    queryKey: jobKeys.stats(),
    queryFn: async () => {
      const response = await jobService.getJobStats()
      return response.data
    },
    staleTime: 5 * 60 * 1000
  })
}

// Hook to fetch job types
export function useJobTypes() {
  return useQuery<ApiResponse<Array<{ value: string; displayName: string }>>, Error>({
    queryKey: [...jobKeys.all, 'jobTypes'],
    queryFn: () => jobService.getJobTypes(),
    staleTime: 30 * 60 * 1000 // 30 minutes - job types don't change often
  })
}

